import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { STORAGE_KEYS, ROUTES } from '@utils/constants';
import { refreshOnce } from './tokenRefresh';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Custom params serializer that handles repeated query params (e.g., group_by=user&group_by=project)
 * instead of the default axios format (e.g., group_by[]=user&group_by[]=project).
 * FastAPI's Query() expects repeated keys without brackets.
 */
export function paramsSerializer(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((v: unknown) => {
        if (v !== null && v !== undefined && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')) {
          searchParams.append(key, String(v));
        }
      });
    } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      searchParams.set(key, String(value));
    }
  });

  return searchParams.toString();
}

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer,
});

// Token refresh state
let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

/**
 * Process queued requests after token refresh
 */
function processQueue(error: Error | null, token: string | null): void {
  refreshQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });
  refreshQueue = [];
}

/**
 * Clear tokens and redirect to login
 */
function handleAuthFailure(): void {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  window.location.href = ROUTES.LOGIN;
}

// Request interceptor for auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor with token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean } | undefined;

    // Only handle 401 errors with a valid original request
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // Don't treat git-provider 401s as session auth failures - these mean the
    // provider credential (PAT) is invalid, not the user's JWT session. Let the
    // error propagate so the UI can direct users to Settings to update their PAT.
    // Covers the multi-provider routes (/git/...) and the legacy GitLab path.
    if (originalRequest.url?.includes('/git/') || originalRequest.url?.includes('/gitlab/')) {
      return Promise.reject(error);
    }

    // Don't retry if this is already a retry or if it's an auth endpoint
    if (originalRequest._retry || originalRequest.url?.includes('/auth/')) {
      handleAuthFailure();
      return Promise.reject(error);
    }

    // Check if we have a refresh token
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) {
      handleAuthFailure();
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err: unknown) => {
          throw err instanceof Error ? err : new Error(String(err));
        });
    }

    // Start refresh
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const newToken = await refreshOnce();
      processQueue(null, newToken);

      // Retry original request with new token
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return await api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError instanceof Error ? refreshError : new Error('Refresh failed'), null);
      handleAuthFailure();
      throw refreshError instanceof Error ? refreshError : new Error('Refresh failed');
    } finally {
      isRefreshing = false;
    }
  }
);
